import DriverBehaviorAPI from 'api/driver.behaviour.api';
import { BaseHandler } from './base.handler';
import {
  setIsLoading,
  setIsLoadingGearMatrix,
  getAll,
  resetTimelineError,
  setTimelineError,
  setGearCountMatrix,
  setPolygonPoints,
  setChartData7D,
  setTripSummary,
} from 'store/driver.behavior.slice';
import { allEvents, allEngineHoursEvents } from 'utils/driverBehavior';
import { type preferredTimeDisplayModeTypes } from 'types/customer.types';
import {
  type DriverBehaviorData,
  type TripSummary,
} from 'types/driver.behavior.types';

export default class DriverBehavorHandler extends BaseHandler {
  private readonly api: DriverBehaviorAPI;

  constructor() {
    super();
    this.api = new DriverBehaviorAPI();
  }

  async getGearCountMatrix(payload: { startDate: string; endDate: string }) {
    try {
      this.dispatch(setIsLoadingGearMatrix(true));

      const cacheKey = `gear_count_matrix_${JSON.stringify(payload)}`;
      const cachedData = this.getCachedData(cacheKey);

      if (cachedData) {
        this.dispatch(setGearCountMatrix(cachedData));
        return cachedData;
      }

      const response = await this.api.getGearCountMatrix({
        startDate: payload.startDate,
        endDate: payload.endDate,
      });

      this.setCachedData(cacheKey, response);

      this.dispatch(setGearCountMatrix(response));
      return response;
    } catch (error) {
      console.error('Error fetching gear count matrix:', error);
      this.dispatch(setTimelineError('Failed to fetch gear count matrix data'));
      throw error;
    } finally {
      this.dispatch(setIsLoadingGearMatrix(false));
    }
  }

  async getPolygonPoints() {
    try {
      const cacheKey = 'polygon_points';
      const cachedData = this.getCachedData(cacheKey);

      if (cachedData) {
        this.dispatch(setPolygonPoints(cachedData));
        return;
      }

      const { polygonPoints, polygonPointsStats } =
        await this.api.getPolygonPoints();

      const data = { polygonPoints, polygonPointsStats };

      this.setCachedData(cacheKey, data);

      this.dispatch(setPolygonPoints(data));
    } catch (error) {
      console.error('Error fetching polygon points for site:', error);
    }
  }

  private generateCacheKey(
    payload: { startDate?: string; endDate?: string; range?: string },
    engineHours: boolean,
    targetValue?: number | string,
    assetType?: string
  ): string {
    // The version suffix retires entries cached before `tripSummary` was stored
    // alongside them — without it a session holding an old entry keeps serving a
    // payload that has no trip counts, and the timeline silently falls back.
    return `driver_behavior_v2_${JSON.stringify(
      payload
    )}_${engineHours}_${targetValue}_${assetType ?? 'all'}`;
  }

  /**
   * Flattens the backend's trip summary into the one shape the timeline reads,
   * accepting either casing for the wrapper and its fields.
   */
  private normalizeTripSummary(raw: any): TripSummary | null {
    const byDevice = raw?.byDevice ?? raw?.by_device;
    if (!Array.isArray(byDevice)) return null;

    return {
      totalTrips: Number(raw?.totalTrips ?? raw?.total_trips ?? 0),
      byDevice: byDevice
        .map((entry: any) => ({
          device: entry?.device ?? entry?.device_id ?? '',
          totalTrips: Number(entry?.totalTrips ?? entry?.total_trips ?? 0),
        }))
        .filter((entry: { device: string }) => entry.device),
    };
  }

  private getCachedData(key: string): any | null {
    try {
      const cachedData = sessionStorage.getItem(key);
      if (cachedData) {
        return JSON.parse(cachedData);
      }
    } catch (error) {
      console.warn('Error reading from cache:', error);
    }
    return null;
  }

  private setCachedData(key: string, data: any): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.warn('Error writing to cache:', error);
    }
  }

  private mapEventForAssetType(event: string, assetType?: string): string {
    if (assetType === 'haul_truck') {
      if (event === 'moving_unloaded') {
        return 'unloaded_trip';
      }
      if (event === 'moving_loaded') {
        return 'loaded_trip';
      }
    }
    return event;
  }

  private mapApiEventToFrontend(event: string, assetType?: string): string {
    if (assetType === 'haul_truck') {
      if (event === 'unloaded_trip') {
        return 'moving_unloaded';
      }
      if (event === 'loaded_trip') {
        return 'moving_loaded';
      }
    }
    return event;
  }

  private isEventAllowedForAssetType(
    event: string,
    assetType?: string
  ): boolean {
    if (assetType === 'haul_truck') {
      if (event === 'moving_loaded' || event === 'moving_unloaded') {
        return false;
      }
    }
    if (assetType === 'loader') {
      if (event === 'loaded_trip' || event === 'unloaded_trip') {
        return false;
      }
    }
    return true;
  }

  private isFleetWideRow(row: Record<string, any>): boolean {
    const deviceId = String(row?.device_id ?? '').toLowerCase();
    return deviceId === 'fleet-wide';
  }

  private inferEventKeyFromRow(row: Record<string, any>): string | null {
    const explicitKey =
      row?.event_key ?? row?.event ?? row?.eventName ?? row?.state;
    if (typeof explicitKey === 'string' && explicitKey.trim()) {
      return explicitKey.trim();
    }

    const sample = Array.isArray(row?.data) ? row.data[0] : undefined;
    if (sample && 'loading_spotting_duration_sec' in sample) {
      return 'loading_spotting';
    }
    if (sample && 'unloading_spotting_duration_sec' in sample) {
      return 'unloading_spotting';
    }

    const derived = String(row?.derived_state ?? sample?.derived_state ?? '')
      .trim()
      .toLowerCase();
    if (!derived) return null;
    if (derived === 'big_shutdown') return 'shutdown';
    if (derived.includes('queuing')) return 'queuing';
    if (derived.includes('loading_spotting')) return 'loading_spotting';
    if (derived.includes('unloading_spotting')) return 'unloading_spotting';
    return derived;
  }

  private normalizeEventRows(rows: any[]): any[] {
    return rows.map((row) => {
      if (!Array.isArray(row?.data)) {
        return row;
      }
      return {
        ...row,
        data: row.data.map((item: Record<string, any>) => ({
          ...item,
          duration_in_secs: Number(
            item?.duration_in_secs ?? item?.duration ?? 0
          ),
        })),
      };
    });
  }

  private normalizeEventBuckets(
    source: Record<string, any[]>,
    frontendEvents: string[]
  ): Record<string, any[]> {
    return frontendEvents.reduce<Record<string, any[]>>((acc, event) => {
      const rows = Array.isArray(source?.[event]) ? source[event] : [];
      acc[event] = this.normalizeEventRows(rows);
      return acc;
    }, {});
  }

  private normalizeFlatDriverBehaviorResponse(
    rows: any[],
    frontendEvents: string[],
    assetType?: string
  ): Record<string, any[]> {
    const normalized = frontendEvents.reduce<Record<string, any[]>>(
      (acc, event) => {
        acc[event] = [];
        return acc;
      },
      {}
    );

    if (!Array.isArray(rows) || rows.length === 0) {
      return normalized;
    }

    let blockIndex = 0;
    rows.forEach((row, index) => {
      const previous = rows[index - 1];
      if (
        index > 0 &&
        this.isFleetWideRow(row) &&
        previous &&
        !this.isFleetWideRow(previous)
      ) {
        blockIndex += 1;
      }

      const inferredKeyRaw = this.inferEventKeyFromRow(row);
      const inferredKey =
        typeof inferredKeyRaw === 'string'
          ? this.mapApiEventToFrontend(inferredKeyRaw, assetType)
          : inferredKeyRaw;
      if (
        typeof inferredKeyRaw === 'string' &&
        !this.isEventAllowedForAssetType(inferredKeyRaw, assetType)
      ) {
        return;
      }
      const fallbackKey =
        frontendEvents[Math.min(blockIndex, frontendEvents.length - 1)];
      const eventKey = inferredKey ?? fallbackKey;

      if (!eventKey || !(eventKey in normalized)) {
        return;
      }
      normalized[eventKey].push(row);
    });

    Object.keys(normalized).forEach((event) => {
      normalized[event] = this.normalizeEventRows(normalized[event]);
    });
    return normalized;
  }

  async getAll(
    payload: {
      startDate?: string;
      endDate?: string;
      range?: string;
    },
    engineHours?: boolean,
    targetValue?: number | string,
    assetType?: string
  ) {
    this.dispatch(resetTimelineError());
    this.dispatch(setIsLoading(true));

    try {
      // Check cache first
      const cacheKey = this.generateCacheKey(
        payload,
        engineHours ?? false,
        targetValue ?? 0,
        assetType
      );
      const cachedData = this.getCachedData(cacheKey);

      if (cachedData) {
        this.dispatch(getAll(cachedData));
        // Cached under its own key: `formattedData` only holds event buckets, so
        // the summary would be lost on every cache hit if it rode along in there.
        this.dispatch(
          setTripSummary(this.getCachedData(`${cacheKey}_trip_summary`))
        );
        return [];
      }

      const eventsToUse = engineHours ? allEngineHoursEvents : allEvents;
      const frontendEvents = eventsToUse.map(({ key }) => key);
      const apiEvents = Array.from(
        new Set([
          ...frontendEvents,
          'loaded_trip',
          'unloaded_trip',
          'moving_loaded',
          'moving_unloaded',
        ])
      );
      const response = await this.api.getAll({
        ...payload,
        events: apiEvents,
        includeAllHaulTrucks: false,
        ...(engineHours ? { showUnproductiveTrips: true } : {}),
      });

      // Sibling of the event keys in the response. Read before the reductions
      // below, all of which keep only the known event buckets and drop the rest.
      // Both casings are accepted because the rest of this payload is snake_case.
      const tripSummary = Array.isArray(response)
        ? null
        : this.normalizeTripSummary(
            response?.tripSummary ?? response?.trip_summary
          );

      const indexedResults = Array.isArray(response?.results)
        ? response.results
        : [];
      const isIndexedResponse = indexedResults.length > 0;
      let formattedData: Record<string, any[]>;

      if (isIndexedResponse) {
        formattedData = frontendEvents.reduce(
          (acc: Record<string, any[]>, event, index) => {
            const eventBucket = indexedResults.find(
              (item: { index: number; data: any[] }) => item?.index === index
            );
            acc[event] = this.normalizeEventRows(eventBucket?.data ?? []);
            return acc;
          },
          {}
        );
      } else if (Array.isArray(response)) {
        // New payload format: flat mixed rows across requested events.
        formattedData = this.normalizeFlatDriverBehaviorResponse(
          response,
          frontendEvents,
          assetType
        );
      } else {
        // Backward compatibility for object responses keyed by event.
        const remappedResponse = Object.entries(response ?? {}).reduce<
          Record<string, any[]>
        >((acc, [event, rows]) => {
          if (!this.isEventAllowedForAssetType(event, assetType)) {
            return acc;
          }
          const frontendEvent = this.mapApiEventToFrontend(event, assetType);
          if (!acc[frontendEvent]) {
            acc[frontendEvent] = [];
          }
          if (Array.isArray(rows)) {
            acc[frontendEvent].push(...rows);
          }
          return acc;
        }, {});
        formattedData = this.normalizeEventBuckets(
          remappedResponse,
          frontendEvents
        );
      }

      this.setCachedData(cacheKey, formattedData);
      this.setCachedData(`${cacheKey}_trip_summary`, tripSummary);

      this.dispatch(getAll(formattedData));
      this.dispatch(setTripSummary(tripSummary));
      return [];
    } catch (_) {
      this.handleError(
        'An error occurred while fetching driver behavior data.'
      );
      this.dispatch(setTimelineError('An error occurred while fetching data'));
    } finally {
      this.dispatch(setIsLoading(false));
    }
    return [];
  }

  async getAllWithPastComparison(
    payload: {
      startDate?: string;
      endDate?: string;
      range?: string;
    },
    engineHours?: boolean,
    targetValue?: number | string,
    assetType?: string
  ) {
    this.dispatch(resetTimelineError());
    this.dispatch(setIsLoading(true));

    try {
      // Check cache first
      const cacheKey = `driver_behavior_with_past_data_${JSON.stringify(
        payload
      )}_${engineHours ?? false}_${targetValue ?? 0}_${assetType ?? 'all'}`;

      const cachedData = this.getCachedData(cacheKey);

      if (cachedData) {
        this.dispatch(getAll(cachedData));
        return [];
      }

      const eventsToUse = engineHours ? allEngineHoursEvents : allEvents;
      const driverData = await Promise.all(
        eventsToUse.map(async ({ key: event }) => {
          const eventForApi = this.mapEventForAssetType(event, assetType);
          const response = await this.api.getAllWithPastComparision({
            ...payload,
            event: eventForApi,
          });
          return { [event]: response ?? [] };
        })
      );

      const formattedData = driverData.reduce((acc, curr) => {
        return { ...acc, ...curr };
      }, {});

      this.setCachedData(cacheKey, formattedData);

      this.dispatch(getAll(formattedData));
      return [];
    } catch (_) {
      this.handleError(
        'An error occurred while fetching driver behavior data.'
      );
      this.dispatch(setTimelineError('An error occurred while fetching data'));
    } finally {
      this.dispatch(setIsLoading(false));
    }
    return [];
  }

  async getChartData7D(
    payload: {
      startDate?: string;
      endDate?: string;
      range?: string;
    },
    engineHours?: boolean,
    targetValue?: number | string,
    assetType?: string
  ) {
    try {
      const cacheKey = `driver_behavior_chart_7d_${JSON.stringify(payload)}_${
        engineHours ?? false
      }_${targetValue ?? 0}_${assetType ?? 'all'}`;

      const cachedData = this.getCachedData(cacheKey);

      if (cachedData) {
        this.dispatch(setChartData7D(cachedData));
        return;
      }

      const eventsToUse = engineHours ? allEngineHoursEvents : allEvents;
      const driverData = await Promise.all(
        eventsToUse.map(async ({ key: event }) => {
          const eventForApi = this.mapEventForAssetType(event, assetType);
          const response = await this.api.getAllWithPastComparision({
            ...payload,
            event: eventForApi,
          });
          return { [event]: response ?? [] };
        })
      );

      const formattedData = driverData.reduce((acc, curr) => {
        return { ...acc, ...curr };
      }, {});

      this.setCachedData(cacheKey, formattedData);
      this.dispatch(
        setChartData7D(formattedData as unknown as DriverBehaviorData)
      );
    } catch (_) {
      console.warn('Failed to fetch 7D chart data');
    }
  }

  async downloadEngineHoursTimeline(
    customerId: string,
    startApiDate: string,
    endApiDate: string,
    preferredTimeDisplayMode: preferredTimeDisplayModeTypes,
    clientTimezone: string,
    returnType: 'excel' | 'pdf',
    isSiteSummary: boolean = false
  ): Promise<ArrayBuffer> {
    try {
      const data = await this.api.downloadEngineHoursTimelineReport(
        customerId,
        startApiDate,
        endApiDate,
        preferredTimeDisplayMode,
        clientTimezone,
        returnType,
        isSiteSummary
      );
      this.handleSuccess(`${returnType} file downloaded`);
      return data;
    } catch (err) {
      this.handleError(`Failed to download ${returnType} file`);
      throw err;
    }
  }

  clearCache(): void {
    const cacheKeys = Object.keys(sessionStorage);
    cacheKeys.forEach((key) => {
      if (key.startsWith('driver_behavior_')) {
        sessionStorage.removeItem(key);
      }
    });
  }
}
